import type { IntegrationOauthResult } from "@playbookai/api-clients";
import { PRODUCT_NAME } from "@playbookai/types";

/**
 * What the completion page is allowed to say.
 *
 * Pure and separate from the page for the same reason `connect-refusal.ts` is:
 * what a surface tells a user is a product decision worth pinning on its own,
 * not a layout detail. It matters more here than there, because this page is
 * PUBLIC — it renders without a session, so every input it has arrived in a URL
 * that anyone can hand to anyone.
 */

/**
 * The shape of a platform error code, and the only thing this page will echo.
 *
 * `sanitisedErrorCode` on the server already collapses anything unexpected to
 * `INTEGRATION_INTERNAL_ERROR`, so a well-formed round trip always matches. The
 * guard is here because a crafted link does not come from the server: without
 * it, "That connection could not be completed (…)" becomes a sentence an
 * attacker finishes, on a page that looks like the product and is reached
 * without signing in. Anything unrecognised is simply omitted — the outcome is
 * still reported, just without a code.
 */
const PLATFORM_ERROR_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;

export function displayableErrorCode(
  errorCode: string | undefined,
): string | null {
  if (errorCode === undefined) return null;
  return PLATFORM_ERROR_CODE.test(errorCode) ? errorCode : null;
}

export interface CompletionMessage {
  readonly title: string;
  readonly detail: string;
  /**
   * `alert` announces assertively; `status` politely. The icon follows it.
   *
   * `cancelled` is a third tone rather than a reuse of `success` (PB-5318).
   * Nothing succeeded, so calling it a success would be its own small lie —
   * and the page reads this to decide the colour it paints, where green would
   * claim a connection that does not exist.
   */
  readonly tone: "success" | "failure" | "cancelled";
}

/**
 * The message for a round trip's result, or for no round trip at all.
 *
 * `null` is the ordinary case of someone opening this URL directly — a bookmark,
 * a back button, a copied link. It must not claim an outcome, because none
 * happened, and it must not look broken either.
 */
export function completionMessage(
  result: IntegrationOauthResult | null,
): CompletionMessage {
  if (result === null) {
    return {
      title: "Nothing to complete",
      detail:
        "This page finishes connecting an account. Open it from your connection settings rather than directly.",
      tone: "failure",
    };
  }

  if (result.outcome === "cancelled") {
    return {
      title: "Sign-in cancelled",
      // Deliberately does NOT say "you cancelled". The outcome is derived from
      // the provider's `access_denied`, which RFC 6749 §4.1.2.1 defines as the
      // resource owner OR THE AUTHORIZATION SERVER denying the request — so an
      // administrator refusing the app, or a disabled OAuth client, arrives
      // here too. "Authorization was not granted" is true in every case that
      // reaches this branch; naming the user as the one who declined would be
      // a claim this page cannot support.
      //
      // The TITLE keeps the product's existing word for it: the attempt row is
      // recorded `cancelled`, and the Admin Console tab behind this one already
      // says "Sign-in cancelled". Two tabs disagreeing about the same event is
      // the defect this change exists to fix.
      detail:
        "Authorization was not granted, so nothing was connected. Your saved configuration is unchanged, and you can reconnect whenever you are ready.",
      tone: "cancelled",
    };
  }

  if (result.outcome === "connected" && result.alreadyConnected) {
    return {
      title: "This account is already connected",
      detail:
        "No new connection was created. Authorization was refreshed for your existing connection. Return to connection settings to use it.",
      tone: "success",
    };
  }

  if (result.outcome === "connected") {
    return {
      title: "Connection complete",
      detail: `You're all set. You can close this tab and return to ${PRODUCT_NAME} — your new connection is ready to use.`,
      tone: "success",
    };
  }

  const code = displayableErrorCode(result.errorCode);
  return {
    title: "Connection failed",
    detail:
      code === null
        ? "Something went wrong while authorizing. Return to connection settings and try again."
        : `Something went wrong while authorizing. Error code: ${code}. Return to connection settings and try again — quote the code if you contact an admin.`,
    tone: "failure",
  };
}
